import {
  Flex,
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Box,
  ButtonProps,
  IconProps,
  BoxProps,
  Portal,
} from '@chakra-ui/react';
import { ChevronDown } from '@kula-ai/icons/dual-tone/ChevronDown';
import React from 'react';

interface KMenuButtonProps extends ButtonProps {
  variant?: 'primary' | 'secondary' | 'tertiary' | string; // Adjust this based on available variants
  triggerContent: React.ReactNode; // The button label or content
  menuContent: React.ReactNode; // The content of the menu
  iconProps?: IconProps; // Additional props for the ChevronDown Icon
  dividerProps?: BoxProps; // Additional props for the divider (Box)
  menuContentProps?: BoxProps;
  menuProps?: any; // Props to pass to Menu component (e.g., gutter, offset)
  showRightIcon?: boolean;
  isPortal?: boolean;
  isOpen?: boolean; // Controlled open state
  onClose?: () => void; // Controlled close handler
}

export const KMenuButton: React.FC<KMenuButtonProps> = ({
  variant = 'primary',
  triggerContent,
  menuContent,
  iconProps,
  dividerProps,
  showRightIcon = true,
  menuContentProps,
  menuProps,
  isPortal = false,
  isOpen,
  onClose,
  ...props
}) => {
  const list = (
    <MenuList zIndex={99} {...menuContentProps}>
      {menuContent}
    </MenuList>
  );

  return (
    <Menu isOpen={isOpen} onClose={onClose} {...menuProps}>
      <MenuButton
        as={Button}
        // The chevron block sits flush against the right edge, so the button
        // gives up its right padding for it — but only when that block is
        // actually rendered. Left unconditional, the `!important` also beat a
        // caller's own `px` and squashed the label against the edge.
        pr={showRightIcon ? '0px !important' : undefined}
        p="0px"
        variant={variant}
        height={'2rem'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        rightIcon={
          showRightIcon && (
            <Flex alignItems="center" justifyContent={'center'}>
              <Box
                borderLeft="1px solid"
                height="16px"
                mr={'6px'}
                h="2rem"
                borderLeftColor={'primary-400'}
                {...dividerProps} // Pass dividerProps to Box
              />
              <Icon
                as={ChevronDown}
                w="16px"
                h="16px"
                mr={'6px'}
                {...iconProps} // Pass iconProps to Icon
              />
            </Flex>
          )
        }
        {...props}
      >
        {triggerContent} {/* Render children as the button content */}
      </MenuButton>
      {isPortal ? <Portal>{list}</Portal> : list}
      {/* Render menuContent as the dropdown */}
    </Menu>
  );
};
